import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import "../styles/QRDetails.css";
import axios from 'axios';
import { GridLoader } from 'react-spinners';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from 'date-fns';

function UpdateBatch() {
  const navigate = useNavigate();
  const [batchno, setBatchno] = useState("");
  // FIX 1: Initialize date states as null (not "") so DatePicker works correctly
  const [manufactureDate, setManufactureDate] = useState(null);
  const [expiryDate, setExpiryDate] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [qrId, setQrId] = useState("");
  const [oldBatch, setOldBatch] = useState("");
  const [comments, setComments] = useState("");
  const [SKU, setSKU] = useState("");
  const [sku_details, setSkuDetails] = useState("");
  const baseURL = sessionStorage.getItem('baseURL');

  useEffect(() => {
    if (sessionStorage.getItem('isLoggedIn') !== "true") {
      navigate('/');
    }
    document.getElementById('loading').hidden = true;
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    let data = JSON.parse(sessionStorage.getItem('batchData'));
    setSkuDetails(JSON.parse(sessionStorage.skuId));
    setQrId(data.qr_id);
    setBatchno(data.batch_no);
    setOldBatch(data.batch_no);
    // FIX 2: Parse string dates from session storage into Date objects for DatePicker
    setManufactureDate(data.dom ? new Date(data.dom) : null);
    setExpiryDate(data.doe ? new Date(data.doe) : null);
    setSKU(data.sku_id);
  };

  const handleUpdateBatch = () => {
    setFieldErrors({});

    const errors = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (!batchno.trim()) {
      errors.batchno = "Batch Number is required.";
    }

    // FIX 3: Validate Date objects directly — no .trim() on dates
    if (!manufactureDate) {
      errors.manufactureDate = "Manufacturing Date is required.";
    } else if (manufactureDate > today) {
      errors.manufactureDate = "Manufacturing Date cannot be a future date.";
    }

    if (!expiryDate) {
      errors.expiryDate = "Expiry Date is required.";
    } else if (manufactureDate && expiryDate <= manufactureDate) {
      errors.expiryDate = "Expiry Date must be after Manufacturing Date.";
    }

    if (!comments.trim()) {
      errors.comments = "Comments is required.";
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    submitBatchDetails();
  };

  function showToast(message, type = "success") {
    const toast = document.createElement("div");
    toast.innerText = message;

    Object.assign(toast.style, {
      position: "fixed",
      top: "20px",
      right: "20px",
      background: type === "error" ? "#d32f2f" : type === "success" ? "#2e7d32" : "#ffa000",
      color: "#fff",
      padding: "12px 18px",
      borderRadius: "6px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
      zIndex: 9999,
      opacity: "0",
      transition: "opacity 0.3s ease"
    });

    document.body.appendChild(toast);
    requestAnimationFrame(() => (toast.style.opacity = "1"));

    setTimeout(() => {
      toast.style.opacity = "0";
      setTimeout(() => toast.remove(), 300);
    }, 3500);
  }

  const submitBatchDetails = () => {
    let sku_temp = JSON.parse(sessionStorage.getItem('skuId'));

    // FIX 4: Format Date objects to "MM-yyyy" strings before sending to API
    const formattedDOM = manufactureDate ? format(manufactureDate, 'yyyy-MM') : null;
    const formattedDOE = expiryDate ? format(expiryDate, 'yyyy-MM') : null;

    const payload = {
      batchNo: batchno,
      dom: formattedDOM,
      doe: formattedDOE,
      qrID: qrId,
      oldBatch: oldBatch,
      // FIX 5: Use template literal correctly — previously `null + " - " + ...` produced "null - ..."
      remarks: `${sessionStorage.getItem('name')} - ${comments}`,
      group_id: sessionStorage.getItem('group_id'),
      name: sessionStorage.getItem('name'),
      designation: "User",
      sku_id: SKU,
      uid: sessionStorage.getItem('qrUid'),
      sku_name: sku_temp.brand_name,
      emp_id: sessionStorage.getItem('username')
    };

    axios.post(baseURL + 'batch/updateBatchDetails', payload, {
      headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
    }).then(() => {
      showToast("Batch Updated Successfully");
      navigate('/batch-management');
    }).catch(() => {
      showToast("Error Updating Batch. Please try again later.", "error");
    });
  };

  return (
    <div className="batch-update-page">

      {/* Sub Header */}
      <div className="sub-header">
        <button
          className="back-btn"
          onClick={() => navigate('/batch-management')}
        >
          ← Back to Batch Management
        </button>
      </div>

      {/* Title */}
      <div className="page-title-band">
        <h1>Update Batch Details</h1>
        {qrId && <span>QR ID: {qrId}</span>}
      </div>

      <div className="batch-layout">

        {/* LEFT */}
        <div className="identity-card">

          <div className="card-band">
            <p className="card-band-title">Batch Identity</p>
          </div>

          <div className="identity-block">

            <div className="identity-row">
              <span className="identity-label">Product Name</span>
              <span className="identity-value">
                {sku_details.brand_name || "—"}
              </span>
            </div>

            <div className="identity-row">
              <span className="identity-label">SKU ID</span>
              <span className="identity-value">
                {SKU || "—"}
              </span>
            </div>

            <div className="identity-row">
              <span className="identity-label">Current Batch</span>
              <span className="identity-value">
                {oldBatch || "—"}
              </span>
            </div>

          </div>

        </div>

        {/* RIGHT */}
        <div className="form-card">

          <div className="card-band">
            <p className="card-band-title">Editable Batch Information</p>
          </div>

          <div className="form-grid">

            {/* Batch Number */}
            <div className="form-group">
              <label>
                Batch Number
                <span className="req">*</span>
              </label>

              <input
                type="text"
                className={fieldErrors.batchno ? "is-invalid" : ""}
                value={batchno}
                placeholder="Enter Batch Number"
                onChange={(e) => {
                  setBatchno(e.target.value);
                  setFieldErrors(prev => ({ ...prev, batchno: "" }));
                }}
              />

              {fieldErrors.batchno && (
                <div className="invalid-feedback">{fieldErrors.batchno}</div>
              )}
            </div>

            {/* Manufacturing Date */}
            {/* FIX 6: Removed minDate={new Date()} — manufacturing dates are in the past */}
            <div className="form-group">
              <label>
                Manufacturing Date
                <span className="req">*</span>
              </label>
              <DatePicker
                selected={manufactureDate}
                onChange={(date) => {
                  setManufactureDate(date);
                  setFieldErrors(p => ({ ...p, manufactureDate: '' }));
                }}
                dateFormat="MMMM yyyy"
                showMonthYearPicker
                showFourColumnMonthYearPicker
                placeholderText="Select month / year"
                minDate={new Date()}
                className={`custom-yearMonth-input form-control ${fieldErrors.manufactureDate ? "is-invalid" : ""}`}
                renderCustomHeader={({
                  date,
                  decreaseYear,
                  increaseYear,
                  prevYearButtonDisabled,
                  nextYearButtonDisabled,
                }) => (
                  <div style={{ color: "white", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 10px" }}>
                    <button onClick={decreaseYear} disabled={prevYearButtonDisabled}>{"<"}</button>
                    <span>{date.getFullYear()}</span>
                    <button onClick={increaseYear} disabled={nextYearButtonDisabled}>{">"}</button>
                  </div>
                )}
              />
              {fieldErrors.manufactureDate && (
                <div className="invalid-feedback d-block">{fieldErrors.manufactureDate}</div>
              )}
            </div>

            {/* Expiry Date */}
            {/* FIX 7: Added missing className="form-group"; minDate tied to manufactureDate */}
            <div className="form-group">
              <label>
                Expiry Date
                <span className="req">*</span>
              </label>
              <DatePicker
                selected={expiryDate}
                onChange={(date) => {
                  setExpiryDate(date);
                  setFieldErrors(p => ({ ...p, expiryDate: '' }));
                }}
                dateFormat="MMMM yyyy"
                showMonthYearPicker
                showFourColumnMonthYearPicker
                placeholderText="Select month / year"
                minDate={manufactureDate || new Date()}
                className={`custom-yearMonth-input form-control ${fieldErrors.expiryDate ? "is-invalid" : ""}`}
                renderCustomHeader={({
                  date,
                  decreaseYear,
                  increaseYear,
                  prevYearButtonDisabled,
                  nextYearButtonDisabled,
                }) => (
                  <div style={{ color: "white", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 10px" }}>
                    <button onClick={decreaseYear} disabled={prevYearButtonDisabled}>{"<"}</button>
                    <span>{date.getFullYear()}</span>
                    <button onClick={increaseYear} disabled={nextYearButtonDisabled}>{">"}</button>
                  </div>
                )}
              />
              {fieldErrors.expiryDate && (
                <div className="invalid-feedback d-block">{fieldErrors.expiryDate}</div>
              )}
            </div>

            {/* Comments */}
            <div className="form-group full">
              <label>
                Comments
                <span className="req">*</span>
              </label>

              <textarea
                className={fieldErrors.comments ? "is-invalid" : ""}
                value={comments}
                placeholder="Enter reason for update"
                onChange={(e) => {
                  setComments(e.target.value);
                  setFieldErrors(prev => ({ ...prev, comments: "" }));
                }}
              />

              {fieldErrors.comments && (
                <div className="invalid-feedback">{fieldErrors.comments}</div>
              )}
            </div>

          </div>

          <div className="form-actions">
            <button
              className="btn-secondary"
              onClick={() => navigate('/batch-management')}
            >
              Cancel
            </button>

            <button
              className="btn-primary"
              onClick={handleUpdateBatch}
            >
              Update Batch
            </button>
          </div>

        </div>

      </div>

      <div className="loading" id="loading">
        <GridLoader
          color="#2C3493"
          loading={true}
          size={20}
        />
      </div>

    </div>
  );
}

export default UpdateBatch;